import { Link } from "react-router-dom";
import { ArrowLeft, ArrowRight, CheckCircle, Phone, Mail, ExternalLink, Wifi, Sliders, Search, Building2, Target, ShieldCheck } from "lucide-react";

const HERO_IMG = "/local-assets/edi-5-0-banner.png";

// I 4 pilastri di assessment (fonte: confcommercioroma.it/servizi-offerti/sportello-edi-5-0/)
const PILASTRI = [
  {
    icon: Wifi,
    titolo: "Assessment del Posizionamento Online",
    payoff: "Migliora la tua presenza sul web e comunica efficacemente con il tuo target.",
    desc: "Valuta la presenza digitale dell'azienda — sito web, canali social, Google My Business — per individuare punti di forza e criticità, fornendo un report dettagliato che funge da base per ulteriori interventi.",
    ctaLabel: "Richiedi un'analisi della tua presenza digitale",
    ctaUrl: "https://forms.zohopublic.eu/ediitsrl/form/Assessmentdelposizionamentoonline1/formperma/XF43gtdcmk9U0YKuQBZalr30xk5bk5JGZGq20eU8BR0",
  },
  {
    icon: ShieldCheck,
    titolo: "Assessment Cybersicurezza",
    payoff: "Proteggi la tua impresa dai rischi informatici e garantisci la sicurezza dei dati.",
    desc: "Misura il livello di protezione della tua azienda contro Cyber Attack, Ransomware e Social Engineering, assicurando la conformità al GDPR. Riceverai un report con i punti deboli da risolvere.",
    ctaLabel: "Richiedi un check-up sulla sicurezza digitale",
    ctaUrl: "https://forms.zohopublic.eu/ediitsrl/form/Assessmentdelrischiocyberz/formperma/_ZUtjRjhJrF6A90KOex69UhqK7OecUywZaPkfzRgb_k",
  },
  {
    icon: Building2,
    titolo: "Assessment Digitale per l'Ospitalità",
    payoff: "Fai evolvere la tua struttura turistica con soluzioni digitali mirate.",
    desc: "Specifico per il settore ricettivo, analizza il grado di maturità digitale dell'azienda individuando soluzioni per migliorare l'esperienza dei clienti e incrementare la competitività.",
    ctaLabel: "Richiedi il tuo check-up digitale",
    ctaUrl: "https://forms.zohopublic.eu/ediitsrl/form/Assessmentdigitaleperlospitalit1/formperma/uuty_c7gs_TikHJDb4efCKNyShQdrtxqHzxq00MoPYw",
  },
  {
    icon: Sliders,
    titolo: "Assessment del Credito e della Sostenibilità",
    payoff: "Migliora il tuo profilo di credito per accedere a finanziamenti vantaggiosi.",
    desc: "Valuta indicatori contabili, profilo di rischio e informazioni ESG della tua azienda per supportarti nell'ottenimento di condizioni di credito favorevoli, con un report dettagliato e linee guida.",
    ctaLabel: "Richiedi la tua analisi finanziaria",
    ctaUrl: "https://forms.zohopublic.eu/ediitsrl/form/Assessmentdelcreditoesostenibilit1/formperma/4whUFNN6fEqRcz3uRfHBT19nKGN_0gro6AxE4t5-ZUg",
  },
];

const BENEFICI = [
  { icon: <Target size={22} className="text-cyan-600" />, titolo: "Supporto gratuito", desc: "4 ambiti chiave della digitalizzazione con consulenze pratiche." },
  { icon: <Search size={22} className="text-cyan-600" />, titolo: "Consulenze personalizzate", desc: "Esperti digitali dedicati e Action Plan su misura." },
  { icon: <Building2 size={22} className="text-cyan-600" />, titolo: "Rete nazionale SPIN", desc: "Accesso alla rete degli Sportelli di Innovazione Confcommercio." },
];

const COPERTURE = [
  { taglia: "Micro e piccole imprese", limite: "fino a 49 dipendenti", copertura: "100%" },
  { taglia: "Medie imprese", limite: "", copertura: "90%" },
  { taglia: "Grandi imprese", limite: "", copertura: "40%" },
];

export default function Edi50Page() {
  return (
    <main id="main-content" data-testid="edi-5-0-page">
      {/* HERO */}
      <section className="relative h-[380px] md:h-[440px] overflow-hidden" aria-labelledby="edi5-title">
        <img src={HERO_IMG} alt="EDI 5.0 — Polo di Innovazione Digitale di Confcommercio" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0" style={{ backgroundColor: "rgba(4,30,66,0.86)" }} />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative h-full flex flex-col justify-end pb-12">
          <Link to="/progetti" className="inline-flex items-center gap-2 text-sm text-white/70 hover:text-white mb-5 transition-colors w-fit" data-testid="back-to-progetti">
            <ArrowLeft size={15} /> Tutti i progetti
          </Link>
          <span className="text-xs font-bold uppercase tracking-[0.2em] text-[#56B7E6] font-['Encode_Sans_Semi_Condensed'] mb-2">
            Innovazione · Nazionale · Concluso
          </span>
          <h1 id="edi5-title" className="text-3xl md:text-4xl lg:text-5xl font-bold text-white font-['Encode_Sans_Semi_Condensed'] tracking-tight">
            EDI 5.0
          </h1>
          <p className="mt-3 text-white/80 text-lg max-w-2xl">
            Il Polo di Innovazione Digitale di Confcommercio per la trasformazione digitale e sostenibile delle PMI italiane.
          </p>
        </div>
      </section>

      {/* INTRO + SIDEBAR */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-3 gap-12">

            <div className="lg:col-span-2 space-y-12">
              {/* Cos'è */}
              <div>
                <span className="text-xs font-bold uppercase tracking-[0.2em] text-[#56B7E6] font-['Encode_Sans_Semi_Condensed']">Cos'è EDI 5.0</span>
                <h2 className="text-2xl font-bold text-[var(--confcom-text-primary)] font-['Encode_Sans_Semi_Condensed'] mt-2 mb-4">
                  Il Polo di Innovazione Digitale di Confcommercio
                </h2>
                <p className="text-lg text-[var(--confcom-text-secondary)] leading-relaxed mb-4">
                  <strong className="text-[var(--confcom-text-primary)]">EDI 5.0</strong> è il Polo di Innovazione Digitale di Confcommercio, creato per guidare le <strong className="text-[var(--confcom-text-primary)]">PMI italiane</strong> nella trasformazione digitale e sostenibile.
                </p>
                <p className="text-[var(--confcom-text-secondary)] leading-relaxed">
                  Un'opportunità innovativa che accompagna le imprese nel loro percorso di digitalizzazione, offrendo un ventaglio integrato di servizi per <strong className="text-[var(--confcom-text-primary)]">analizzare, orientare e monitorare</strong> il processo di trasformazione. L'iniziativa si articola in diverse fasi operative, ognuna fondamentale per diagnosticare le potenzialità digitali e individuare le soluzioni più adatte.
                </p>
              </div>

              {/* Pilastri */}
              <div>
                <span className="text-xs font-bold uppercase tracking-[0.2em] text-[#56B7E6] font-['Encode_Sans_Semi_Condensed']">Assessment</span>
                <h2 className="text-2xl font-bold text-[var(--confcom-text-primary)] font-['Encode_Sans_Semi_Condensed'] mt-2 mb-5">
                  I 4 Pilastri del Servizio
                </h2>
                <p className="text-[var(--confcom-text-secondary)] leading-relaxed mb-6">
                  Analisi approfondite per ottenere una visione chiara della situazione digitale dell'impresa. <strong className="text-[var(--confcom-text-primary)]">In caso di adesione è possibile indicare la propria preferenza per un massimo di due servizi.</strong>
                </p>
                <div className="grid sm:grid-cols-2 gap-5">
                  {PILASTRI.map((p) => {
                    const Icon = p.icon;
                    return (
                      <div key={p.titolo} className="p-5 rounded-xl border border-[var(--confcom-border)] hover:shadow-md transition-all">
                        <div className="flex items-center gap-3 mb-3">
                          <div className="w-10 h-10 rounded-lg bg-cyan-50 flex items-center justify-center shrink-0">
                            <Icon size={20} className="text-cyan-600" aria-hidden="true" />
                          </div>
                          <h3 className="text-base font-bold text-[var(--confcom-text-primary)] font-['Encode_Sans_Semi_Condensed'] leading-tight">{p.titolo}</h3>
                        </div>
                        <p className="text-sm italic text-[var(--confcom-text-secondary)] mb-2">{p.payoff}</p>
                        <p className="text-sm text-[var(--confcom-text-secondary)] leading-relaxed mb-4">{p.desc}</p>
                        <a href={p.ctaUrl} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 text-xs font-semibold text-[var(--confcom-blue)] hover:underline" data-testid={`edi5-cta-${p.titolo.replace(/\W+/g, "-").toLowerCase()}`}>
                          {p.ctaLabel} <ExternalLink size={12} aria-hidden="true" />
                        </a>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Orientamento e Consulenza */}
              <div>
                <span className="text-xs font-bold uppercase tracking-[0.2em] text-[#56B7E6] font-['Encode_Sans_Semi_Condensed']">Fase successiva</span>
                <h2 className="text-2xl font-bold text-[var(--confcom-text-primary)] font-['Encode_Sans_Semi_Condensed'] mt-2 mb-4">
                  Orientamento e Consulenza
                </h2>
                <p className="text-[var(--confcom-text-secondary)] leading-relaxed mb-4">
                  Sulla base dei risultati degli assessment vengono organizzati incontri di <strong className="text-[var(--confcom-text-primary)]">consulenza personalizzata</strong>. Durante questa fase viene redatto un <strong className="text-[var(--confcom-text-primary)]">Action Plan</strong> su misura, che propone soluzioni tecnologiche e digitali specifiche, supportate da consulenti ed esperti. Gli incontri possono essere svolti sia online sia in presenza.
                </p>
                <p className="text-[var(--confcom-text-secondary)] leading-relaxed">
                  EDI 5.0 offre <strong className="text-[var(--confcom-text-primary)]">condizioni di finanziamento vantaggiose</strong> con coperture in base alla dimensione dell'impresa.
                </p>

                <div className="grid sm:grid-cols-3 gap-3 mt-6">
                  {COPERTURE.map((c) => (
                    <div key={c.taglia} className="p-5 rounded-xl border border-cyan-100 bg-cyan-50/40 text-center">
                      <div className="text-3xl font-bold text-cyan-700 font-['Encode_Sans_Semi_Condensed']">{c.copertura}</div>
                      <div className="text-sm font-semibold text-[var(--confcom-text-primary)] mt-2">{c.taglia}</div>
                      {c.limite && <div className="text-xs text-[var(--confcom-text-muted)] mt-1">{c.limite}</div>}
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Sidebar */}
            <div className="space-y-6">
              <div className="bg-cyan-50 border border-cyan-100 rounded-2xl p-6">
                <h3 className="text-sm font-bold uppercase tracking-widest text-cyan-700 mb-4">Benefici principali</h3>
                <div className="space-y-4">
                  {BENEFICI.map((b, i) => (
                    <div key={i} className="flex items-start gap-3">
                      <div className="w-9 h-9 bg-white rounded-lg flex items-center justify-center shrink-0 shadow-sm">{b.icon}</div>
                      <div>
                        <div className="text-sm font-bold text-[var(--confcom-text-primary)] font-['Encode_Sans_Semi_Condensed']">{b.titolo}</div>
                        <div className="text-xs text-[var(--confcom-text-secondary)] leading-relaxed mt-0.5">{b.desc}</div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="bg-[var(--confcom-bg-blue-light)] border border-[var(--confcom-border)] rounded-2xl p-6">
                <h3 className="text-sm font-bold uppercase tracking-widest text-[var(--confcom-blue)] mb-4">Per maggiori informazioni</h3>
                <div className="space-y-3 text-sm text-[var(--confcom-text-secondary)]">
                  <div className="flex items-start gap-2">
                    <Phone size={16} className="text-[var(--confcom-blue)] shrink-0 mt-0.5" aria-hidden="true" />
                    <a href="tel:+390668437630" className="hover:text-[var(--confcom-blue)] transition-colors">06.68437630-353</a>
                  </div>
                  <div className="flex items-start gap-2">
                    <Mail size={16} className="text-[var(--confcom-blue)] shrink-0 mt-0.5" aria-hidden="true" />
                    <a href="mailto:spin@confcommercioroma.it" className="hover:text-[var(--confcom-blue)] transition-colors break-all">spin@confcommercioroma.it</a>
                  </div>
                </div>
                <a href="https://www.ediconfcommercio.it/edi-5-0/" target="_blank" rel="noopener noreferrer" className="mt-5 inline-flex items-center gap-1.5 text-xs font-semibold text-[var(--confcom-blue)] hover:underline">
                  Sito ufficiale EDI 5.0 <ExternalLink size={12} aria-hidden="true" />
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Footer nav */}
      <section className="py-10 bg-[#F5F8FC] border-t border-[var(--confcom-border)]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex flex-wrap items-center justify-between gap-4">
          <Link to="/progetti" className="inline-flex items-center gap-2 text-sm text-[var(--confcom-text-secondary)] hover:text-[var(--confcom-blue)] transition-colors">
            <ArrowLeft size={15} /> Tutti i progetti
          </Link>
          <Link to="/contatti" className="inline-flex items-center gap-2 bg-[var(--confcom-blue)] text-white px-5 py-2.5 rounded-md font-medium hover:opacity-90 transition-opacity text-sm">
            Contattaci <ArrowRight size={14} />
          </Link>
        </div>
      </section>
    </main>
  );
}
